/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import styled from '@emotion/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Paper, Stack } from '@mui/material';
import { AxiosError } from 'axios';
import { isEmpty } from 'lodash-es';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, FieldValues, UseFormProps } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  ICandidate,
  IJob,
  ISubmissionSettingsResponse,
  uploadFileToS3,
  getJobsFromAdvancedQuery,
  JobAssignmentStatusFilter,
  getJobByJobCode,
  getSubmissionsByCandidateIdAndJobId,
  ISubmissionStageStatusSetting,
  IUserDTO,
} from 'wisestep-api';
import {
  JobPreference,
  LocationInfo,
  PersonalInfo,
  SalaryDetails,
  WorkExpForm,
  DocumentInfo,
  WsButton,
  IConversationState,
  AssessmentInfo,
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  Spinner,
  WsColors,
  WsDrawer,
} from 'wisestep-base';

import { UpdateStatusModal } from '../../../components';
import { useBusinessUnitContext, useUserContext } from '../../../context';
import { updateWorkAuthorizationCategory } from '../../../features/candidateLayout/components/childComponets/CandidateProfileDetails/UpdateCandiadteDetailsHelper';
import CardWithAccordion from '../../../features/candidateLayout/components/childComponets/CardWithAccordion';
import useCandidateCV from '../../../features/jobs/listing/hooks/useCandidateCV';
import LinearProgressWithLabel from '../../../utils/LinearProgressWithLabel';
import { warning, success, failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import openConversationWidget from '../../../utils/openConversationWidget';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import CandidateProfileDetailsEdit from '../../candidateLayout/components/childComponets/CandidateProfileDetails/CandidateProfileDetailsEdit';
import AddContactLink from '../../candidateLayout/components/childComponets/CandidateProfileDetails/components/AddContactLink';
import EmailPopUp from '../../candidateLayout/components/EmailPopUp';
import { getProfessionalDetails } from '../../candidateLayout/utils/CandidateHelper';
import { useSubmissionStageStatus } from '../../crmSettings/hooks/useSubmissionStageStatus';
import StageStatusContextProvider from '../../submissions/context/StageStatusContext';
import { getMandatoryFields, submissionSchema } from '../schema/formSchema';
import IOtherDocument from '../types/IOtherDocument';
import IResume from '../types/IResume';
import { SubmissionType } from '../types/SubmissionTypes';
import { ITrackCvPages, downloadCV, trackCV } from '../utils/cvHelper';
import { getBuId, submissionFormDefaultValues } from '../utils/fieldDefaultValues';
import {
  addCandidateNote,
  createSubmissionEntity,
  onError,
  saveSubmission,
  updateSubmission,
} from '../utils/saveSubmission';
import useCheckExistingCandidate from '../utils/useCheckExistingCandidate';

import BusinessUnit from './BusinessUnit';
import CVViewModal from './CVViewModal';
import OtherSubmissionDetails from './OtherSubmissionDetails';
import SubmissionFormNewFields from './SubmissionFormNewFields';
import SubmitOptions from './SubmitOptions';
import WidgetJobManagement from './WidgetJobManagement';

interface ISubmissionForm {
  submissionSetting: ISubmissionSettingsResponse;
  state: IConversationState;
  selectedJob?: IJob | null;
  submissionPurpose: string;
  closeDrawer: () => void;
}

export interface IOpenCvView {
  open: boolean;
  url: string | null;
}
const SubmissionForm: FC<ISubmissionForm> = ({
  submissionSetting,
  state,
  selectedJob,
  submissionPurpose = 'update',
  closeDrawer,
}) => {
  const [duplicatedCheck, setDuplicateCheck] = useState(false);
  const [jobForUpdate, setJobForUpdate] = useState<IJob | null>(null);
  const setLatestResume = useConversationWidgetStore((state) => state.setLatestResume);
  const activityConversation = useConversationWidgetStore((state) => state.activityConversation);
  const view = useConversationWidgetStore((state) => state.view);
  const activityConversationFor = useConversationWidgetStore(
    (state) => state.activityConversationFor
  );
  const [stateInitialized, setStateInitialized] = useState<boolean>(false);
  const [openStatusModal, setOpenStatusModal] = useState<boolean>(false);
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const completeCandidateDetails = activityConversation ?? { ...state.candidate } ?? {};
  const [candidate, setCandidate] = useState<ICandidate | null>({
    ...completeCandidateDetails,
    otherDocuments: [],
  });
  const [openCvView, setOpenCvView] = useState<IOpenCvView>({
    open: false,
    url: state.candidate.resumeLocation,
  });
  const [otherDocument, setOtherDocument] = useState<IOtherDocument[]>([]);
  const [currentSubmissionType, setCurrentSubmissionType] = useState<SubmissionType | null>(null);

  const updateOpenWidget = openConversationWidget((state) => state.updateOpenWidget);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const latestResume = useConversationWidgetStore((state) => state.latestResume);
  const resume = useRef<IResume | null>(null);
  const buContext = useBusinessUnitContext();
  const { submissionStageData } = useSubmissionStageStatus();
  const formOptions: UseFormProps<any> = {
    resolver: yupResolver(submissionSchema(submissionSetting, buContext.length > 1 ? true : false)),
    defaultValues: submissionFormDefaultValues(
      completeCandidateDetails,
      selectedJob ?? jobForUpdate,
      buContext
    ),
    mode: 'onChange',
  };
  const methods = useForm(formOptions);
  const [openEditCandidate, setOpenEditCandidate] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openEmail, setOpenEmail] = useState<boolean>(false);
  const [submissionEmailBody, setSubmissionEmailBody] = useState<string>('');
  const closeUpdateStatusModal = () => {
    setOpenStatusModal(false);
    updateOpenWidget(false);
    window.location.reload();
  };
  const navigate = useNavigate();

  const loggedInUser = useUserContext().userDTO as IUserDTO;

  const loggedInUserName =
    loggedInUser?.firstName?.trim() || loggedInUser?.lastName?.trim() || loggedInUser?.email || '';

  const [CVOptions] = useCandidateCV(candidate ?? ({} as ICandidate));
  const checkForDuplicateSubmission = useCallback(
    async (data: ICandidate | FieldValues) => {
      try {
        if (activityConversation && data?.candidateId && jobForUpdate?.id) {
          const response = await getSubmissionsByCandidateIdAndJobId(
            data.candidateId,
            jobForUpdate.id
          );
          setDuplicateCheck(true);
          if (response?.data) {
            return true;
          }
          return false;
        } else if (!activityConversation && data.candidateId && selectedJob?.id) {
          const response = await getSubmissionsByCandidateIdAndJobId(
            data.candidateId,
            selectedJob.id
          );
          setDuplicateCheck(true);
          if (response?.data) {
            return true;
          }
          return false;
        }
        return false;
      } catch (e) {
        return false;
      }
    },

    [activityConversation, jobForUpdate?.id, selectedJob?.id]
  );

  useEffect(() => {
    const jobCode = activityConversation?.jobCode;
    if (jobCode) {
      (async () => {
        const response = await getJobsFromAdvancedQuery({
          jobCodes: [jobCode],
          page: 0,
          size: 2,
          sort: ['scoringData.score,desc'],
          searchAcross: ['MY_TEAMS', 'MY_PERSONAL_DATA'],
          jobAssignmentStatusFilter: JobAssignmentStatusFilter.Everything,
          jobStatus: 'All',
        });

        if (!isEmpty(response.data) && response.data[0]) {
          setJobForUpdate(response.data[0]);
          methods.reset(
            submissionFormDefaultValues(completeCandidateDetails, response.data[0], buContext)
          );
        }
        if (isEmpty(response.data)) {
          const jobResponse = await getJobByJobCode(jobCode);
          setJobForUpdate(jobResponse.data);
          methods.reset(
            submissionFormDefaultValues(completeCandidateDetails, jobResponse.data, buContext)
          );
        }
      })();
    }
  }, [activityConversation]);

  useEffect(() => {
    if (!stateInitialized) {
      if (!isEmpty(activityConversation) && activityConversation && latestResume != null) {
        activityConversation.resumeFileOriginalName = latestResume?.name;
        activityConversation.resumeLocation = latestResume?.url;
      } else if (!isEmpty(state.candidate) && state.candidate && latestResume != null) {
        state.candidate.resumeFileOriginalName = latestResume?.name;
        state.candidate.resumeLocation = latestResume?.url;
      }
      const candidatedata = activityConversation ?? state.candidate ?? {};
      setCandidate({ ...candidatedata, otherDocuments: [] });
    }
  }, [activityConversation, candidate, latestResume, state.candidate, stateInitialized]);

  useEffect(() => {
    if (!duplicatedCheck) {
      (async () => {
        if (submissionPurpose === 'save' && activityConversationFor == 'mapping' && candidate) {
          const duplicateSubmission = await checkForDuplicateSubmission(candidate);
          duplicateSubmission && setIsDuplicate(true);
        }
        if (candidate?.resumeLocation == null && candidate?.resume) {
          candidate.resumeLocation = candidate?.resume?.resumeLocation;
          candidate.resumeFileOriginalName = candidate?.resume?.resumeFileOriginalName;
          setCandidate({ ...candidate });
        }
        setStateInitialized(true);
      })();
    }
  }, [
    activityConversationFor,
    candidate,
    checkForDuplicateSubmission,
    duplicatedCheck,
    submissionPurpose,
  ]);

  const {
    watch,
    getValues,
    reset,
    formState: { errors },
  } = methods;
  const { mandatoryFields, shownFields } = getMandatoryFields(
    submissionSetting,
    buContext.length > 1
  );
  const [linearProgressValue, setLinearProgressValue] = useState<number>(0);
  const watchedValues = watch(mandatoryFields);
  const otherDetails = getProfessionalDetails(candidate ?? ({} as ICandidate)).filter(
    (field) => field.value !== 'NA' && !shownFields.includes(field.title)
  );

  useEffect(() => {
    let validFields = mandatoryFields.reduce((count, field) => {
      if (getValues(field) && !errors[field]) {
        count++;
      }
      return count;
    }, 0);
    if (
      mandatoryFields.includes('otherDocuments') &&
      candidate?.otherDocuments &&
      candidate.otherDocuments.length > 0
    ) {
      validFields++;
    }
    if (CVOptions?.length > 0 && mandatoryFields.includes('cvUpload')) {
      validFields++;
    }
    const count = (validFields / mandatoryFields.length) * 100;
    setLinearProgressValue(count);
  }, [watchedValues, errors, mandatoryFields, getValues, candidate?.otherDocuments, CVOptions]);

  const socialLinks = watch('socialLinks');
  const { openMergeCandidate, setOpenMergeCandidate, existingCandidate, newSocialLink } =
    useCheckExistingCandidate(socialLinks, candidate);

  const validateCV = (candidate: any) => {
    if (!candidate?.resumeLocation) {
      setNotification(warning('Please Upload Cv for the candidate'));
      return false;
    }
    return true;
  };

  const validateJobSelection = (selectedJob: any) => {
    if (!selectedJob) {
      setNotification(warning('Please select job first'));
      return false;
    }
    return true;
  };

  const validateDuplicateSubmission = async (data: FieldValues): Promise<boolean> => {
    const duplicateSubmission = await checkForDuplicateSubmission(data);
    if (duplicateSubmission) {
      setDisableSubmit(false);
      setIsDuplicate(true);
      return false;
    }
    return true;
  };

  const handlePopoverClose = () => setAnchorEl(null);

  const emailDrawerClose = () => {
    setOpenEmail(false);
    setCurrentSubmissionType(null);
    closeDrawer();
  };

  const handleSubmitClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    setDisableSubmit(true);
    const anchorElement = event.currentTarget;
    console.log('Form Errors:', methods.formState.errors);
    const isValid = await methods.trigger();
    if (!isValid) {
      setNotification(warning(onError(methods.formState.errors)));
      setDisableSubmit(false);
      return;
    }
    if (!validateJobSelection(selectedJob)) {
      setDisableSubmit(false);
      return;
    }
    setAnchorEl(anchorElement);
    setDisableSubmit(false);
  };

  const onFormSubmit = (
    submitType: SubmissionType,
    recruiter: any,
    proceed: boolean,
    comment: string,
    emailBody: string
  ) => {
    const formData = methods.getValues();
    setCurrentSubmissionType(submitType);
    const submissionSettings =
      (submissionStageData.find(
        (item) => item.submissionType === submitType
      ) as Partial<ISubmissionStageStatusSetting>) || {};
    const submissionData = {
      ...formData,
      questionResponse: candidate?.questionResponse,
      submitType,
      assignedRecruiter: submitType === SubmissionType.INTERNAL ? recruiter?.email ?? '' : null,
      assignToUserName: submitType === SubmissionType.INTERNAL ? recruiter?.name ?? '' : null,
      applicationStage: submissionSettings?.applicationStage ?? 'CV Submitted',
      applicationState: submissionSettings?.applicationState ?? 'Feedback Pending',
      comment: comment ?? '',
      proceed: proceed || false,
      emailBody: emailBody ?? '',
      workAuthorizationCategory: updateWorkAuthorizationCategory(formData),
    };
    submit(submissionData);
    handlePopoverClose();
  };

  const update = async (data: FieldValues, conversation: ICandidate) => {
    const response = await updateSubmission(
      data,
      conversation,
      otherDocument,
      resume.current,
      selectedJob
    );
    handleSubmissionResponse(response, data);
  };

  const submitWithValidJob = async (data, job, candidate) => {
    addCandidateNote(loggedInUser?.email ?? '', candidate, data?.comment, loggedInUserName);
    const response = await saveSubmission(data, candidate, job, otherDocument, resume.current);
    if (response) {
      setCandidate(response);
    }
    handleSubmissionResponse(response, data);
  };

  const handleSubmissionResponse = (response: Error | AxiosError, data: FieldValues) => {
    if (!response) {
      setDisableSubmit(false);
      setNotification(failed('Submission failed'));
      return;
    }

    const submission = Array.isArray(response) ? response[0] : response;

    setNotification(success('Submission Update'));

    if (data?.submitType === SubmissionType.EXTERNAL) {
      setSubmissionEmailBody(submission?.submissionEmailBody ?? '');
      setOpenEmail(true);
    } else {
      navigateToSubmission(submission?.candidateId);
    }
  };

  const validateBeforeSubmit = async (
    data: FieldValues,
    candidate: any,
    selectedJob: any
  ): Promise<boolean> => {
    if (!validateCV(candidate) || !validateJobSelection(selectedJob)) {
      setDisableSubmit(false);
      return false;
    }
    if (!(await validateDuplicateSubmission(data))) {
      return false;
    }
    return true;
  };

  const updateResumeDetails = (candidate: ICandidate) => {
    const selectedCV = CVOptions[Number(getValues('cv'))] ?? {};
    const resumeDetails = {
      resumeFileOriginalName: selectedCV.documentFileName || '',
      resumeLocation: selectedCV.documentLocation || '',
      resumeUploadedOn: selectedCV.documentUploadedOn
        ? new Date(selectedCV.documentUploadedOn)
        : new Date(),
    };

    Object.assign(candidate, resumeDetails);
    resume.current = { ...resumeDetails };

    candidate.mappedCandidateResumes = [
      {
        candidateMappingId: '',
        email: '',
        gitHubLink: '',
        id: '',
        linkedInLink: '',
        phone: '',
        pinCode: '',
        ...resumeDetails,
      },
    ];
  };

  const createSubmissionPayload = (): ICandidate | null => {
    if (!candidate) return null;
    updateResumeDetails(candidate);
    return createSubmissionEntity(
      methods.getValues(),
      candidate,
      selectedJob,
      otherDocument,
      resume.current
    );
  };

  const submit = async (data: FieldValues) => {
    if (!candidate || !data?.proceed) return;
    updateResumeDetails(candidate);
    if (
      (activityConversation &&
        activityConversationFor === 'submission' &&
        !candidate.isJobCandidateMapping) ||
      submissionPurpose === 'update'
    ) {
      return update(data, candidate);
    }

    if (!(await validateBeforeSubmit(data, candidate, selectedJob))) return;

    submitWithValidJob(data, selectedJob, candidate);
  };

  const navigateToSubmission = (candidateId?: string) => {
    if (!candidateId) return;
    closeDrawer?.();
    navigate(
      `/candidate-layout/submissions?id=${candidateId}&array=${[candidateId]}&sendMail=true`
    );
  };

  const onUpload = async (file: File[], field: string) => {
    const url = await uploadFileToS3(file[0]);
    const resumeLink = url.split('?')[0];
    if (field === 'document') {
      const alteredCandidate = { ...state.candidate };
      const alteredDocuments = [...otherDocument];
      alteredDocuments.push({
        documentLocation: resumeLink,
        originalName: file[0].name,
        tag: null,
        uploadedOn: new Date(),
      });
      setOtherDocument([...alteredDocuments]);
      alteredCandidate.otherDocuments = alteredDocuments;
      setCandidate({ ...alteredCandidate });
    }
  };
  const resumeAction = (
    action: string,
    url: string | null = CVOptions[getValues('cv')]?.documentLocation ?? '',
    docName?: string | null
  ) => {
    switch (action) {
      case 'view':
        !isEmpty(url) && trackCV('view', url, ITrackCvPages.SUBMISSION);
        setOpenCvView({ open: true, url: url });
        break;
      case 'download':
        if (!isEmpty(url)) {
          trackCV('download', url, ITrackCvPages.SUBMISSION);
          url && downloadCV(url, docName ?? CVOptions[getValues('cv')]?.documentFileName ?? '');
        }
        break;
      case 'removeCandidate':
        if (candidate) {
          setLatestResume(null);
          const alteredCandidate = { ...candidate };
          alteredCandidate.resumeFileOriginalName = '';
          alteredCandidate.resumeLocation = '';
          alteredCandidate.resumeUploadedOn = null;
          setCandidate({ ...alteredCandidate });
        }
        break;
      case 'removeOtherDocument':
        if (candidate) {
          const alteredCandidate = { ...candidate };
          alteredCandidate.otherDocuments = alteredCandidate?.otherDocuments?.filter(
            (document) => document.documentLocation != url
          );
          setCandidate({ ...alteredCandidate });
        }
        break;
    }
  };
  const onEditClick = () => setOpenEditCandidate(true);
  const formatCVOptions = useMemo(
    () =>
      (CVOptions || []).map((option, index) => ({
        label: option?.documentFileName || '',
        value: index,
      })),
    [CVOptions]
  );
  const updateCandidate = (data: ICandidate) => {
    setCandidate(data);
    reset(submissionFormDefaultValues(data, selectedJob, buContext));
  };

  return (
    <>
      {stateInitialized ? (
        <>
          {!isDuplicate ? (
            <FormProvider {...methods}>
              <form onKeyDown={(e) => e.code === 'Enter' && e.preventDefault()}>
                <Grid item xs={12} p={1} px={2} sx={{ background: WsColors.White }}>
                  <Grid container>
                    <Grid item xs={3}>
                      <Box mt={1}>
                        <WsTypography variant={WsTypoVariants.TITLE} bold={true}>
                          Review Submission
                        </WsTypography>
                      </Box>
                    </Grid>
                    <Grid item xs={9}>
                      {view != 'submission' && (
                        <Box sx={{ float: 'right' }}>
                          <WidgetJobManagement
                            selectedJob={
                              activityConversation
                                ? jobForUpdate
                                  ? jobForUpdate
                                  : null
                                : selectedJob
                            }
                            jobFetchFailure={
                              !isEmpty(activityConversation) && isEmpty(jobForUpdate)
                            }
                            ss={12}
                            hideRemove={true}
                          />
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    px: 1.5,
                    pb: 1.5,
                    background: WsColors.TertiaryShades100,
                    maxHeight: '90vh',
                    overflowY: 'auto',
                  }}
                >
                  <Paper>
                    <Grid
                      item
                      xs={12}
                      px={2.5}
                      py={1.25}
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        background: WsColors.White,
                      }}
                    >
                      <LinearProgressWithLabel
                        value={linearProgressValue}
                        sx={{ height: 10, borderRadius: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <PersonalInfo
                        showTitle={false}
                        candidate={candidate ?? ({} as ICandidate)}
                        onUpload={(file: File[]) => onUpload(file, 'resume')}
                        submissionSetting={submissionSetting}
                        resumeAction={resumeAction}
                        CVOptions={formatCVOptions}
                        onEditClick={onEditClick}
                      />
                      <LocationInfo submissionSetting={submissionSetting} showTitle={false} />
                      <WorkExpForm submissionSetting={submissionSetting} showTitle={false} />
                      <JobPreference
                        watch={watch}
                        submissionSetting={submissionSetting}
                        showTitle={false}
                      />
                      <SalaryDetails
                        submissionSetting={submissionSetting}
                        showTitle={false}
                        job={selectedJob ?? jobForUpdate ?? undefined}
                        isOfferStage={
                          submissionPurpose == 'update' &&
                          ['Offer', 'Joining'].includes(
                            completeCandidateDetails?.applicationStage ?? ''
                          )
                        }
                      />
                      <AssessmentInfo submissionSetting={submissionSetting} showTitle={false} />
                      <DocumentInfo
                        candidate={candidate}
                        submissionSetting={submissionSetting}
                        onUpload={(file: File[]) => onUpload(file, 'document')}
                        resumeAction={resumeAction}
                      />
                      <Grid pb={2}>
                        <BusinessUnit
                          submissionSetting={submissionSetting}
                          name="buId"
                          defaultValue={getBuId(candidate, buContext)}
                        />
                      </Grid>
                      <SubmissionFormNewFields submissionSetting={submissionSetting} />
                      <Grid pb={2}>
                        <CardWithAccordion
                          title="Other details"
                          topChild={<></>}
                          bottomChild={<OtherSubmissionDetails details={otherDetails} />}
                          onEditClick={onEditClick}
                          hasData={!isEmpty(otherDetails)}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
                <StyledGrid>
                  <WsButton type="button" disabled={disableSubmit} onClick={handleSubmitClick}>
                    {activityConversation && activityConversationFor == 'submission'
                      ? 'Update'
                      : 'Submit'}
                  </WsButton>
                </StyledGrid>
                {selectedJob && (
                  <SubmitOptions
                    anchorEl={anchorEl}
                    onClose={handlePopoverClose}
                    onInternalSubmit={(rec, comment, emailBody) =>
                      onFormSubmit(SubmissionType.INTERNAL, rec, true, comment, emailBody)
                    }
                    onExternalSubmit={(proc, emailBody) =>
                      onFormSubmit(SubmissionType.EXTERNAL, null, proc, '', emailBody)
                    }
                    getSubmissionPayload={createSubmissionPayload}
                    job={selectedJob}
                  />
                )}
              </form>
              {openCvView.open && (
                <CVViewModal
                  open={openCvView.open}
                  s3url={openCvView.url}
                  handleClose={() => setOpenCvView({ open: false, url: openCvView.url })}
                />
              )}
            </FormProvider>
          ) : (
            <Stack
              direction="column"
              spacing={2}
              mt={4}
              justifyContent="center"
              alignItems="center"
            >
              <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.ERROR}>
                Duplicate Candidate
              </WsTypography>
              <WsTypography>{`This Candidate is already submitted for the job ${
                activityConversation ? jobForUpdate?.title : selectedJob?.title
              } (${
                activityConversation ? jobForUpdate?.jobCode : selectedJob?.jobCode
              })`}</WsTypography>
            </Stack>
          )}
          {openStatusModal && candidate && (
            <StageStatusContextProvider>
              <UpdateStatusModal
                candidate={candidate}
                openStatusModal={openStatusModal}
                onClose={closeUpdateStatusModal}
              />
            </StageStatusContextProvider>
          )}
        </>
      ) : (
        <Spinner />
      )}
      {openEditCandidate && (
        <>
          <WsDrawer
            open={openEditCandidate}
            title="Edit Profile"
            style={{ zIndex: '1300' }}
            onClose={() => setOpenEditCandidate(false)}
            size="large"
          >
            <CandidateProfileDetailsEdit
              close={() => setOpenEditCandidate(false)}
              candidate={candidate ?? ({} as ICandidate)}
              updateCandidate={updateCandidate}
            />
          </WsDrawer>
        </>
      )}
      {openEmail && candidate && selectedJob && submissionEmailBody && (
        <EmailPopUp
          candidate={candidate}
          job={selectedJob}
          openEmail={openEmail}
          onClose={emailDrawerClose}
          mailBody={submissionEmailBody}
          submissionType={currentSubmissionType}
        />
      )}
      {openMergeCandidate && existingCandidate && candidate && (
        <AddContactLink
          open={openMergeCandidate}
          onClose={() => setOpenMergeCandidate(false)}
          isEmail={false}
          isPhone={false}
          isMerge={true}
          existingCandidate={existingCandidate}
          newEmail={newSocialLink ?? ''}
          candidate={candidate}
        />
      )}
    </>
  );
};

export default SubmissionForm;

const StyledGrid = styled(Grid)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  borderTop: `1px solid ${WsColors.TertiaryShades200}`,
  position: 'fixed',
  background: 'white',
  bottom: 0,
  zIndex: 3,
  width: '-webkit-fill-available',
  height: '4rem',
  gap: '1rem',
  paddingRight: 2,
}));
